import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.js";
import { ThemeContext } from "../../constant/theme.js";
import { LanguageContext } from "../../constant/LanguageContext.js"; // ✅ استورد LanguageContext
import { useModal } from '../../constant/ModalContext.jsx';

// Constants
import translations from "./constants/User_translations.js";
import { getUserNavItems, getAdminNavItems, pageToModuleMap } from "./constants/navItems.js";

// Hooks
import useUnreadMessages from "./hooks/useUnreadMessages.js";
import useLocation from "./hooks/useLocation.js";
import useModulePermissions from "./hooks/useModulePermissions.js";

// Components
import ThemeToggle from "./components/ThemeToggle.jsx";
import BackgroundDecor from "./components/BackgroundDecor.jsx";

// Routes
import UserRoutes from "./routes/UserRoutes.jsx";
import AdminRoutes from "./routes/AdminRoutes.jsx";

// Styles
import { buildContainerStyles, buildGlobalStyles } from "./styles/globalStyles.js";

// Other imports
import { mo3tiThemes } from "../../components/mo3tiThemes.js";
import SidebarWithTopbar from "../(All) Sidebar/SidebarWithTopbar";
import ViewTypeSwitcher from '../../components/ViewTypeSwitcher';

const Mo3tiUserProfile = () => {
  // ============ HOOKS AND STATE ============
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { theme, setTheme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext); // âœ… Ø§Ø­ØµÙ„ Ø¹Ù„Ù‰ Ø§Ù„Ù„ØºØ©
  const isRTL = language === 'ar'; // âœ… Ø­Ø¯Ø¯ Ø§ØªØ¬Ø§Ù‡ Ø§Ù„ØµÙØ­Ø©
  const t = translations[language]; // âœ… Ø§Ø­ØµÙ„ Ø¹Ù„Ù‰ Ø§Ù„ØªØ±Ø¬Ù…Ø§Øª
  const { showModal } = useModal();

  // State management
  const [notifications, setNotifications] = useState(3);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(true);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('viewType') || 'user';
  });

  // This shell is dual-mode (UserRoutes vs AdminRoutes below) but only reads
  // viewType once on mount — react to programmatic view switches too (e.g. a
  // notification click that belongs to the other view fires
  // 'managerViewTypeChanged' after updating storage).
  useEffect(() => {
    const syncViewMode = () => {
      setViewMode(localStorage.getItem('viewType') || 'user');
    };
    window.addEventListener('managerViewTypeChanged', syncViewMode);
    window.addEventListener('storage', syncViewMode);
    return () => {
      window.removeEventListener('managerViewTypeChanged', syncViewMode);
      window.removeEventListener('storage', syncViewMode);
    };
  }, []);

  const [currentPage, setCurrentPage] = useState(() => {
    return viewMode === 'user' ? 'professionalProfile' : 'dashboard';
  });

  // Custom hooks
  const { unreadMessages } = useUnreadMessages(BASE_URL);
  const { userLocation, locationError, loading } = useLocation(t);
  const { modulePermissions, loadingPermissions } = useModulePermissions(BASE_URL);

  // Check if user has an admin level assigned
  const hasAdminLevel = () => {
    try {
      const adminLevelData = localStorage.getItem('adminLevel');
      if (adminLevelData) {
        const adminLevel = JSON.parse(adminLevelData);
        return adminLevel && (adminLevel.modules?.length > 0 || adminLevel.adminLevelId);
      }
    } catch (e) {
      console.error('Error checking admin level:', e);
    }
    return false;
  };

  // Toggle between admin and manager view
  const toggleViewMode = () => {
    if (!hasAdminLevel()) {
      console.warn('User does not have admin level assigned');
      return;
    }
    const newMode = viewMode === 'admin' ? 'manager' : 'admin';
    setViewMode(newMode);
    localStorage.setItem('userViewMode', newMode);

    // Navigate based on view mode
    if (newMode === 'admin') {
      // Navigate to admin configuration page
      navigate('/admin-level-configuration');
    } else {
      // Navigate back to user profile
      navigate('/userprofile/professionalProfile');
    }
  };


  // ============ HELPER FUNCTIONS ============
  const getLocalStorageItem = (key, defaultValue = "") => {
    try {
      const item = localStorage.getItem(key);
      return item || defaultValue;
    } catch (error) {
      console.error(`Error accessing localStorage for key: ${key}`, error);
      return defaultValue;
    }
  };

  const createAvatar = (name) => {
    if (!name) return "UN";
    const nameParts = name.split(" ");
    const firstLetter = nameParts[0]?.[0] || "";
    const secondLetter = nameParts[1]?.[0] || "";
    return (firstLetter + secondLetter).toUpperCase();
  };

  // ============ DATA CONFIGURATION ============
  const currentTheme = isDarkMode ? mo3tiThemes.dark : mo3tiThemes.light;
  // "Communicator Module (PTT & Private Messages)" is the umbrella toggle a
  // superadmin actually reaches for in Communication Modules — it wasn't
  // wired to anything, so flipping it left the Messenger page locked unless
  // the separate, easy-to-miss "Private Messaging" sub-toggle was ALSO set.
  const isPrivateMessagingEnabled =
    (modulePermissions?.privateMessaging || modulePermissions?.communicatorModule) ?? false;

  // Navigation menu items based on view
  const userNavItems = getUserNavItems(t, isPrivateMessagingEnabled, unreadMessages);
  const adminNavItems = getAdminNavItems(t, isPrivateMessagingEnabled);

  // Select nav items based on viewMode
  const allNavItems = viewMode === 'user' ? userNavItems : adminNavItems;  // Changed from 'manager'

  useEffect(() => {
    // Extract the last part of the URL path to determine the active page
    const pathParts = routerLocation.pathname.split('/').filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];

    if (lastPart) {
      // Find a matching nav item for the current path
      const matchedNavItem = allNavItems.find(
        (item) => item.page.toLowerCase() === lastPart.toLowerCase()
      );

      if (matchedNavItem) {
        setCurrentPage(matchedNavItem.page);
      } else {
        // If the path doesn't match a known nav item for this view,
        // or if it's a root path, fallback to the default page.
        setCurrentPage(viewMode === 'user' ? 'professionalProfile' : 'dashboard');
      }
    } else {
      setCurrentPage(viewMode === 'user' ? 'professionalProfile' : 'dashboard');
    }
  }, [routerLocation.pathname, viewMode, allNavItems]);

  // Filter navigation items based on admin level permissions.
  // Three-state (v2): HIDDEN removes the item, VISIBLE_ACCESS_DENIED keeps it
  // visible with a lock suffix (still clickable), VISIBLE_ACCESSIBLE is normal.
  const { getPageState } = useAuth();
  const navItems = allNavItems
    .filter(item => {
      // Always visible items in both views
      if (viewMode === 'manager') {
        // Manager view: always show profile and settings, hide admin-only pages
        if (item.page === "professionalProfile" || item.page === "settings") {
          return true;
        }
        return true;
      }

      // Admin view: filter based on admin level permissions
      if (viewMode === 'admin') {
        // Dashboard and Settings always visible
        if (item.page === "dashboard" || item.page === "Settings") {
          return true;
        }

        // Get the required module for this page
        const requiredModule = pageToModuleMap[item.page];

        // If no module mapping exists, show the item (backward compatibility)
        if (!requiredModule) return true;

        // Show the item unless the page is HIDDEN for this user
        return getPageState(requiredModule) !== 'HIDDEN';
      }

      return true;
    })
    .map(item => {
      if (viewMode !== 'admin') return item;
      const requiredModule = pageToModuleMap[item.page];
      if (!requiredModule) return item;
      return getPageState(requiredModule) === 'VISIBLE_ACCESS_DENIED'
        ? { ...item, accessDenied: true }
        : item;
    });

  // Employee data with safe localStorage access
  const employeeData = {
    id: getLocalStorageItem("infinityCode", "EMP001"),
    name: getLocalStorageItem("employeeName", "Ù…ÙˆØ¸Ù ØºÙŠØ± Ù…Ø­Ø¯Ø¯"),
    position: "Senior Developer",
    department: "Information Technology",
    email: getLocalStorageItem("email", "employee@company.com"),
    phone: "+20" + getLocalStorageItem("employeePhone", "1234567890"),
    address: "123 Tahrir Square, Cairo, Egypt",
    joinDate: getLocalStorageItem("HiringDate", "2024-01-01"),
    employeeId: getLocalStorageItem("clientCode") || getLocalStorageItem("infinityCode", "EMP001"),
    manager: "Ahmed Magdy",
    status: "Active",
    avatar: createAvatar(getLocalStorageItem("employeeName", "Ù…ÙˆØ¸Ù ØºÙŠØ± Ù…Ø­Ø¯Ø¯")),
    birthDate: getLocalStorageItem("DoB", "1990-01-01"),
    nationalId: getLocalStorageItem("nationalId", "12345678901234"),
    emergencyContact: "Emergency Contact Info",
    workLocation: getLocalStorageItem("location", "Cairo"),
    firstLevelManager: getLocalStorageItem("firstLineManagerInfinityCode", "MGR001")
  };

  // Salary and benefits data
  const salaryData = {
    basicSalary: parseFloat(getLocalStorageItem("netSalary", "10000")) || 10000,
    allowances: 3000,
    deductions: 2500,
    netSalary: parseFloat(getLocalStorageItem("netSalary", "10000")) || 10000,
    lastPayDate: "2024-03-30",
    ytdEarnings: 306000,
    taxDeducted: 30000,
    insurance: 12000
  };

  // Attendance statistics
  const attendanceData = {
    presentDays: 22,
    absentDays: 1,
    lateDays: 2,
    workingDays: 25,
    attendanceRate: 88,
    totalHours: 176,
    averageHours: 8.8
  };

  // ============ EVENT HANDLERS ============
  const handleViewTypeChange = (type) => {
    // This shell renders BOTH the User view (UserRoutes) and the Admin view
    // (AdminRoutes) in place for the same /userprofile/* route (see the
    // viewMode checks below) - a real "user"-role account can never reach
    // /userprofilemanager or /userprofileadmin (PrivateRoute gates those to
    // manager/admin accounts only), so navigating there always got bounced
    // back - leaving viewMode/localStorage set to a view that was never
    // actually granted, which is what caused the switcher and the rendered
    // content to disagree. "user" and "admin" only ever need a local state
    // change; there is no "manager" view reachable from this shell.
    setViewMode(type);
    localStorage.setItem('viewType', type);
  };

  const handleLogout = () => {
    try {
      logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login");
    }
  };

  const handleSwitchManager = () => {
    try {
      // "/dashboard" alone lands the shell with no page segment selected —
      // target the actual "dashboard" page, matching how every other
      // "go to admin dashboard" navigation in the app does it.
      navigate("/dashboard/dashboard");
    } catch (error) {
      console.error("switch error:", error);
      navigate("/userprofile");
    }
  };

  const handlePageChange = (page) => {
    const navItem = navItems.find(item => item.page === page);
    if (navItem?.locked) {
      const title = (t && t.notice) || 'Notice';
      const message = navItem.lockMessage || undefined; // allow ModalContext to use localized default
      showModal({ title, message, options: { requestAccessEndpoint: process.env.REACT_APP_BASE_URL ? `${process.env.REACT_APP_BASE_URL}/api/request-module-access` : null, module: navItem.page } });
      return;
    }
    setCurrentPage(page);
    navigate(`/userprofile/${page}`);
  };

  const toggleTheme = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    if (setTheme) {
      setTheme(newDarkMode ? "dark" : "light");
    }
  };

  // ============ CREATE PAGE PROPS ============
  const pageProps = {
    employeeData,
    salaryData,
    attendanceData,
    theme: currentTheme,
    isDarkMode
  };

  // ============ STYLES ============
  const containerStyles = buildContainerStyles(currentTheme, isRTL);
  const globalStyles = buildGlobalStyles(currentTheme, isRTL, sidebarOpen);

  if (loadingPermissions) {
    return (
      <div style={{ ...containerStyles, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ color: 'white', fontSize: '18px' }}>Loading permissions...</div>
      </div>
    );
  }

  // ============ MAIN RENDER ============
  return (
    <div style={containerStyles}>
      {/* Background Elements */}
      <BackgroundDecor currentTheme={currentTheme} />

      {/* Sidebar with Topbar */}
      <SidebarWithTopbar
        navItems={navItems}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        user={user}
        notifications={notifications}
        handleLogout={handleLogout}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        themeToggle={<ThemeToggle theme={theme} toggleTheme={toggleTheme} />}
        viewModeSwitcher={
          <ViewTypeSwitcher
            viewType={viewMode}
            onSelect={handleViewTypeChange}  // Use new handler
            hasAdminLevel={hasAdminLevel}
            role={localStorage.getItem('role')}
          />
        }
      >
        <div style={{
          padding: "0",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          marginTop: currentPage === "messages" ? 0 : 40,
        }}>
          {viewMode === 'user' && (
            <UserRoutes
              key={viewMode}
              pageProps={pageProps}
              isPrivateMessagingEnabled={isPrivateMessagingEnabled}
              toggleTheme={toggleTheme}
              userLocation={userLocation}
              locationError={locationError}
              t={t}
            />
          )}

          {viewMode === 'admin' && (
            <AdminRoutes
              key={viewMode}
              pageProps={pageProps}
              isPrivateMessagingEnabled={isPrivateMessagingEnabled}
              toggleTheme={toggleTheme}
            />
          )}
        </div>
      </SidebarWithTopbar>

      {/* Global Styles */}
      <style>{globalStyles}</style>
    </div>
  );
};

export default Mo3tiUserProfile;